import React, { FC, useState } from 'react';
import { navigate } from 'gatsby';
import { compact } from 'lodash';
import { Trans } from '@lingui/macro';
import LoginDialog from '@/components/Common/LoginDialog';
import DownloadDialog from '@/components/Common/DownloadDialog';
import DownloadButton from '@/components/Common/DownloadButton';
import Button, { ButtonVariant } from '@/components/Common/Button';
import { useAuthentication } from '@/context/Authentication';
import { isOfType } from '@/utils/user';
import { UserType } from '@/types/index';
import routes from '@/routes';
import * as styles from './TechnicalSpecs.module.scss';

type Props = {
  sku?: string;
  files?: Record<string, string | null> | null;
  specsheet?: Queries.DatoCmsDownloadFragment | null;
};

const TechnicalSpecs: FC<Props> = ({ sku, files, specsheet }) => {
  const { user } = useAuthentication();
  const [isLoginDialogOpen, setLoginDialogOpen] = useState(false);
  const [isDownloadDialogOpen, setDownloadDialogOpen] = useState(false);

  const canDownload = user && isOfType(user, UserType.Library);
  const handleLoginSuccess = () => {
    setLoginDialogOpen(false);
    setDownloadDialogOpen(true);
  };

  const handleTechnicalDocumentationClick = () => {
    setDownloadDialogOpen(true);
  };

  const handleClickHereClick = () => {
    if (!user) {
      setLoginDialogOpen(true);
    } else {
      navigate(routes.upgradeAccount({ to: UserType.Library }));
    }
  };

  const hasFiles =
    compact(Object.entries(files || {}).filter(([_, value]) => value)).length > 0;

  return hasFiles || specsheet ? (
    <>
      <div className={styles.main}>
        <div className={styles.buttons}>
          {specsheet ? (
            <DownloadButton
              className={styles.button}
              file={specsheet}
              title={<Trans>Technical Documentation</Trans>}
            />
          ) : null}

          {hasFiles ? (
            <Button
              className={styles.button}
              variant={ButtonVariant.Ghost}
              disabled={!canDownload}
              onClick={handleTechnicalDocumentationClick}
            >
              <Trans>2D/3D Files</Trans>
            </Button>
          ) : null}
        </div>

        {!canDownload ? (
          <p className={styles.accountRequirements}>
            <Trans>
              You need a Library Account or log in to get the technical documentation. If
              you want to update your account or log in
            </Trans>{' '}
            <span className={styles.link} role="button" onClick={handleClickHereClick}>
              <Trans>click here</Trans>
            </span>
          </p>
        ) : null}
      </div>
      <LoginDialog
        isOpen={isLoginDialogOpen}
        onSuccess={handleLoginSuccess}
        onClose={() => setLoginDialogOpen(false)}
      />
      <DownloadDialog
        files={files}
        sku={sku}
        isOpen={isDownloadDialogOpen}
        onClose={() => setDownloadDialogOpen(false)}
      />
    </>
  ) : null;
};

export default TechnicalSpecs;
